import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../firebase/AuthContext";
import Sidebar from "../../../components/Sidebar";
import { getApiUrl } from "../../../config/api";

import SettingsTab from "./tabs/SettingsTab";
import EmbedTab from "./tabs/EmbedTab";
import ConversationsTab from "./tabs/ConversationTab";
import LeadsTab from "./tabs/LeadsTab";
import CorsTab from "./tabs/CorsTab";
import UsageBillingTab from "./tabs/UsageBillingTab";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import ChatWidgetV2 from "../../../embed/ChatWidget/chatWidget";

const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

const LANG_LABEL = { en: "English", "zh-tw": "繁體中文", zh: "繁體中文" };
const langLabel = (l) => LANG_LABEL[l] || l || "English";

// ── Top-level tab config ────────────────────────────────────────────────────────
const MAIN_TABS = {
  bots: {
    label: "My Bots",
    iconPath:
      "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
  },
  quickstart: {
    label: "Quick Start",
    iconPath:
      "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
  },
};

// ── Sub-tab config (inside manage modal) ─────────────────────────────────────────
// A tab shows when the user holds any of its roles; Embed Code comes with
// being able to see the bot at all.
const MANAGE_TABS = [
  { key: "settings", label: "Settings", roles: ["webchatbot.settings.edit", "webchatbot.delete"] },
  { key: "embed", label: "Embed Code", roles: null },
  { key: "conversations", label: "Conversations", roles: ["webchatbot.conversations.read"] },
  { key: "leads", label: "Contacts", roles: ["webchatbot.leads.read"] },
  { key: "cors", label: "CORS Setting", roles: ["webchatbot.cors.edit"] },
  {
    key: "usage",
    label: "Usage & Billing",
    roles: ["webchatbot.billing.read", "webchatbot.billing.manage", "billing.view_all"],
  },
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

const MainTabButton = ({ tabKey, config, activeTab, onTabChange }) => {
  const isActive = activeTab === tabKey;
  return (
    <button
      onClick={() => onTabChange(tabKey)}
      className={`w-full flex items-center justify-center gap-2 px-5 md:px-8 py-2 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
        isActive
          ? "bg-orange-500 text-white"
          : "text-gray-600 hover:bg-slate-300"
      }`}
    >
      <svg
        className="w-5 h-5 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={config.iconPath}
        />
      </svg>
      <span>{config.label}</span>
    </button>
  );
};

// Underline tabs — used inside the manage modal, distinct from the main-page pills.
const ModalTabButton = ({ label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`relative flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
      active
        ? "border-orange-500 text-orange-500"
        : "border-transparent text-gray-500 hover:text-gray-700"
    }`}
  >
    {label}
    {badge > 0 && (
      <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 shrink-0">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
  </button>
);

const Spinner = () => (
  <div className="flex justify-center py-8">
    <svg
      className="animate-spin h-8 w-8 text-orange-500"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

// ─── Bot cards ──────────────────────────────────────────────────────────────────

const BotCard = ({ bot, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-lg hover:border-orange-300 transition-all"
  >
    <div className="flex items-center gap-3 mb-4">
      <span
        className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg font-bold shrink-0"
        style={{ backgroundColor: bot.primary_color }}
      >
        {bot.bot_name?.charAt(0)?.toUpperCase() || "B"}
      </span>
      <div className="min-w-0">
        <p className="font-semibold text-gray-800 truncate">{bot.bot_name}</p>
        <p className="text-xs text-gray-400 font-mono truncate">{bot.bot_id}</p>
      </div>
    </div>
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: bot.primary_color }}
        />
        {langLabel(bot.language)}
      </span>
      <span className="text-xs font-medium text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">
        Customize →
      </span>
    </div>
  </button>
);

const CreateBotCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center justify-center gap-2 min-h-[136px] rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-400 hover:border-orange-400 hover:text-orange-500 dark:hover:border-orange-500 transition-colors"
  >
    <span className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center text-2xl leading-none">
      +
    </span>
    <span className="text-sm font-medium">Create new bot</span>
  </button>
);

// ─── Quick Start panel ────────────────────────────────────────────────────────

const QuickStartPanel = ({ onCreateBot }) => (
  <div className="max-w-2xl mx-auto">
    <p className="text-sm text-gray-500 dark:text-gray-200 mb-6 text-center">
      Get your bot live in under 2 minutes.
    </p>

    <div className="space-y-4">
      {[
        {
          step: "1",
          title: "Create a bot",
          desc: "Go to the My Bots tab and click the Create new bot card. Give it a name, pick a brand colour, and choose the language.",
        },
        {
          step: "2",
          title: "Copy the embed code",
          desc: "Open a bot card → Embed Code tab. Copy the script block and paste it into your website just before the closing </body> tag.",
        },
        {
          step: "3",
          title: "Your bot is live",
          desc: "Refresh your website — the chat widget appears immediately. No deployment, no build step required.",
        },
        {
          step: "4",
          title: "Monitor conversations",
          desc: "Open a bot card → Conversations tab to see every session in real time. Click a session to read the full message history.",
        },
      ].map((s) => (
        <div
          key={s.step}
          className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-white"
        >
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {s.step}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-0.5">
              {s.title}
            </p>
            <p className="text-sm text-gray-500">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>

    {onCreateBot && (
      <div className="mt-6 text-center">
        <button
          onClick={onCreateBot}
          className="px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
        >
          Create my first bot →
        </button>
      </div>
    )}
  </div>
);

// ─── Create Bot modal ──────────────────────────────────────────────────────────

const CreateBotModal = ({ token, onCreated, onClose }) => {
  const [form, setForm] = useState({
    bot_name: "",
    primary_color: "#E78A24",
    language: "en",
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!form.bot_name.trim()) {
      setError("Bot name is required.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const res = await axios.post(BOT_API + "/", form, {
        headers: authHeader(token),
      });
      if (res.data.success) {
        onCreated(res.data.data);
      }
    } catch {
      setError("Failed to create bot. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Create New Bot</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bot Name *
            </label>
            <input
              type="text"
              value={form.bot_name}
              onChange={(e) => setForm({ ...form, bot_name: e.target.value })}
              placeholder="e.g. My Support Bot"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary Colour
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) =>
                  setForm({
                    ...form,
                    primary_color: e.target.value,
                  })
                }
                className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <span className="text-sm text-gray-500 font-mono">
                {form.primary_color}
              </span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Default language
            </label>
            <select
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-900"
            >
              <option value="en">English</option>
              <option value="zh-tw">Traditional Chinese (繁體中文)</option>
            </select>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create Bot"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Manage Bot modal ────────────────────────────────────────────────────────────

const ManageBotModal = ({ bot, token, language, onClose, onSaved, onDeleted }) => {
  const { hasRole } = useAuth();
  const tabs = MANAGE_TABS.filter((t) => !t.roles || hasRole(t.roles));
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [unreadLeads, setUnreadLeads] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] min-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-base font-bold shrink-0"
              style={{ backgroundColor: bot.primary_color }}
            >
              {bot.bot_name?.charAt(0)?.toUpperCase() || "B"}
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-800 truncate">
                {bot.bot_name}
              </h2>
              <p className="text-xs text-gray-400 font-mono truncate">
                ID: {bot.bot_id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <button
              onClick={() => setShowPreview((p) => !p)}
              title="Preview the live widget (bottom-right)"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                showPreview
                  ? "bg-orange-500 border-orange-500 text-white hover:bg-orange-600"
                  : "border-orange-500 text-orange-500 hover:bg-orange-50"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 01-4-.836L3 20l1.09-3.272C3.399 15.65 3 13.872 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              {showPreview ? "Hide preview" : "Try it live"}
            </button>
            <button
              onClick={onClose}
              title="Close"
              className="text-gray-400 hover:text-gray-700 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Sub-tab bar */}
        <div className="px-6 shrink-0 bg-gray-100 border-b border-gray-200">
          <div className="flex gap-1 ">
            {tabs.map((t) => (
              <ModalTabButton
                key={t.key}
                label={t.label}
                active={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                badge={t.key === "leads" ? unreadLeads : 0}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div
          className={`flex-1 overflow-y-auto ${
            activeTab === "leads" ? "py-6" : "p-6"
          }`}
        >
          {activeTab === "settings" && (
            <SettingsTab
              bot={bot}
              token={token}
              onSaved={onSaved}
              onDeleted={onDeleted}
            />
          )}
          {activeTab === "embed" && <EmbedTab bot={bot} />}
          {activeTab === "conversations" && (
            <ConversationsTab
              bot={bot}
              token={token}
              isActive={activeTab === "conversations"}
            />
          )}
          {activeTab === "leads" && (
            <LeadsTab
              bot={bot}
              isActive={activeTab === "leads"}
              onUnreadCountChange={setUnreadLeads}
              token={token}
            />
          )}
          {activeTab === "cors" && (
            <CorsTab bot={bot} token={token} onSaved={onSaved} />
          )}
          {activeTab === "usage" && (
            <UsageBillingTab bot={bot} language={language} />
          )}
        </div>
      </div>

      {/* Live widget preview — floats above the modal (z-9999). Uses saved config. */}
      {showPreview && (
        <>
          <div className="fixed bottom-28 right-6 z-[9998] max-w-[220px] bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
            This is exactly what your visitors see. Click the bubble to chat.
          </div>
          <ChatWidgetV2
            key={bot.bot_id}
            botId={bot.bot_id}
            botName={bot.bot_name}
            primaryColor={bot.primary_color}
            language={bot.language}
            avatarId={bot.avatar_id}
            welcomeMessage={bot.welcome_message}
            stylePreset={bot.style_preset}
            starterPrompts={(bot.starter_prompts || []).filter(Boolean)}
          />
        </>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

function BotDashboard() {
  const { currentUser, hasRole } = useAuth();
  const canCreate = hasRole("webchatbot.create");
  const navigate = useNavigate();
  const location = useLocation();

  const [bots, setBots] = useState([]);
  const [mainTab, setMainTab] = useState("bots");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [manageBot, setManageBot] = useState(null);
  const [idToken, setIdToken] = useState("");

  // Language toggle (URL-synced). No page-level i18n yet — content stays English.
  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };
  const [language, setLanguage] = useState(getLangFromUrl());

  useEffect(() => {
    setLanguage(getLangFromUrl());
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentUser) navigate("/login");
  }, [currentUser, navigate]);

  const fetchBots = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      setIdToken(token);
      const res = await axios.get(BOT_API + "/", {
        headers: authHeader(token),
      });
      if (res.data.success) {
        setBots(res.data.bots || []);
      }
    } catch (err) {
      console.error("[BotDashboard] Failed to fetch bots:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  const handleBotCreated = (newBot) => {
    setBots((prev) => [...prev, newBot]);
    setShowCreateModal(false);
    setMainTab("bots");
    setManageBot(newBot);
  };

  const handleBotSaved = (updatedBot) => {
    setBots((prev) =>
      prev.map((b) => (b.bot_id === updatedBot.bot_id ? updatedBot : b)),
    );
    setManageBot((prev) =>
      prev && prev.bot_id === updatedBot.bot_id ? updatedBot : prev,
    );
  };

  const handleBotDeleted = (botId) => {
    setBots((prev) => prev.filter((b) => b.bot_id !== botId));
    setManageBot(null);
  };

  if (!currentUser) return null;

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans overflow-hidden">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col overflow-hidden pt-12">
        <div className="flex-1 overflow-y-auto">
          <div className="w-5/6 mx-auto px-4 lg:px-8 py-8">
            {/* ── Page Title ──────────────────────────────────── */}
            <div className="relative mb-8 lg:mb-12 text-center">
              <h1 className="text-3xl lg:text-4xl font-bold text-black dark:text-white mb-2">
                Bot Platform
              </h1>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                Create, customise, and manage AI chat bots for your websites.
              </p>
            </div>

            {/* ── Tab Navigation ──────────────────────────────── */}
            <div className="flex justify-center mb-8 w-full">
              <div className="w-full flex justify-between gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 bg-gray-100 shadow-xl rounded-xl">
                {Object.entries(MAIN_TABS).map(([key, config]) => (
                  <MainTabButton
                    key={key}
                    tabKey={key}
                    config={config}
                    activeTab={mainTab}
                    onTabChange={setMainTab}
                  />
                ))}
              </div>
            </div>

            {/* ── Tab Content ─────────────────────────────────── */}
            <div className="animate-fadeIn">
              {mainTab === "bots" ? (
                loading ? (
                  <Spinner />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {bots.map((b) => (
                      <BotCard
                        key={b.bot_id}
                        bot={b}
                        onClick={() => setManageBot(b)}
                      />
                    ))}
                    {canCreate && (
                      <CreateBotCard onClick={() => setShowCreateModal(true)} />
                    )}
                  </div>
                )
              ) : (
                <QuickStartPanel
                  onCreateBot={
                    canCreate
                      ? () => {
                          setMainTab("bots");
                          setShowCreateModal(true);
                        }
                      : null
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create bot modal */}
      {showCreateModal && (
        <CreateBotModal
          token={idToken}
          onCreated={handleBotCreated}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Manage bot modal */}
      {manageBot && (
        <ManageBotModal
          key={manageBot.bot_id}
          bot={manageBot}
          token={idToken}
          language={language}
          onClose={() => setManageBot(null)}
          onSaved={handleBotSaved}
          onDeleted={handleBotDeleted}
        />
      )}
    </div>
  );
}

export default BotDashboard;
